import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as turf from '@turf/turf';
import * as Map from '../common/map';

/**
 * Whether the baseline steps offer the on-map edit button.
 * It was held back on the grounds that the confirm/redo panel already owns the moment the line is
 * drawn. That moment is exactly when a wrong vertex is noticed, though, and until now the only
 * answer to one was to throw the whole line away and draw it again - on a coastline of twenty
 * points, over one of them. The button sits on the map while the panel asks to confirm, so the
 * choice reads as confirm, redo, or fix this point.
 * The edit is validated the same way the drawing is: the line has to stay inside the area of
 * interest, and the shape has to remain one Earth Engine will accept.
 * @see {useShapeVertexEditing}
 */
export const BASELINE_VERTEX_EDITING_ENABLED = true;

/**
 * @function defaultValidate
 * @description The validation every AOI step shares: the shape must still have enough points and
 * must not cross over itself. Google Maps happily lets a vertex be dragged across one of the
 * shape's own edges, but the resulting bow-tie is a geometry Earth Engine either refuses or
 * measures wrongly, so it has to be caught here.
 * @param {Array<Array<Number>>} coords - The shape's coordinates, as [lng, lat] pairs.
 * @param {('polygon'|'rectangle'|'polyline')} type - The shape's type.
 * @param {Function} t - i18next translation function.
 * @returns {String} The translated error message, or an empty string when the shape is valid.
 */
const defaultValidate = (coords, type, t) => {
  if (!coords || coords.length < 3) return t('forms.map.editTooFewPoints');
  // A rectangle can only ever stay a rectangle, so it has nothing to cross.
  if (type === 'rectangle') return '';

  try {
    const ring = [...coords, coords[0]];
    if (turf.kinks(turf.polygon([ring])).features.length > 0) return t('forms.map.editSelfIntersection');
  } catch (e) {
    // A ring turf itself rejects is not one Earth Engine will accept either.
    console.warn('Could not validate the edited shape:', e);
    return t('forms.map.editSelfIntersection');
  }

  return '';
};

/**
 * @function useShapeVertexEditing
 * @description Adds "edit the points of an already-drawn shape" to a map step, without each step
 * having to repeat the wiring: it owns the edit on/off state, the vertex handles, the validation of
 * the resulting geometry and the map control the toggle button is rendered into.
 *
 * Only the points the shape already has can be moved — no new ones are added — which is why the
 * handles come from `Map.enableVertexEditing` rather than Google Maps' own `editable` mode.
 *
 * The caller drives it with three calls: `setShape` whenever the shape is (re)created, `stopEditing`
 * before committing the step, and rendering `<ShapeEditControl>` with the returned object.
 * @param {Object} options
 * @param {Function} options.onChange - Receives the shape's new `[lng, lat]` pairs after each drag.
 * @param {Function} [options.validate] - `(coords, type, t) => errorMessage`, replacing the default
 * "enough points and no self-intersection" check. Return an empty string when the shape is valid.
 * @param {Number} [options.position] - `google.maps.ControlPosition` for the button; TOP_CENTER by default.
 * @param {Boolean} [options.enabled] - False keeps the button off the map, for steps that already
 * own that moment with a modal of their own. Defaults to true.
 * @see {@link Map.enableVertexEditing} - draws and wires the draggable vertex handles.
 * @see {ShapeEditControl} - renders the toggle button into `controlContainer`.
 * **Called by:
 * @see {AOIChooser} - each module's area-of-interest step.
 * @returns {{isEditing: Boolean, geometryError: String, controlContainer: HTMLElement|null,
 * hasShape: Boolean, setShape: Function, stopEditing: Function, toggleEdit: Function}}
 */
export const useShapeVertexEditing = ({ onChange, validate, position, enabled = true } = {}) => {
  const [t] = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [geometryError, setGeometryError] = useState('');
  const [controlContainer, setControlContainer] = useState(null);
  const [hasShape, setHasShape] = useState(false);

  const overlayRef = useRef(null);
  const typeRef = useRef('polygon');
  const stopHandlesRef = useRef(null);
  const containerRef = useRef(null);
  const removeControlRef = useRef(null);
  // Kept in refs so the vertex-drag listeners, which are registered once when editing starts,
  // always call the callbacks from the latest render instead of the ones they closed over.
  const onChangeRef = useRef(onChange);
  const validateRef = useRef(validate);

  useEffect(() => {
    onChangeRef.current = onChange;
    validateRef.current = validate;
  });

  useEffect(
    () => () => {
      stopHandlesRef.current?.();
      removeControlRef.current?.();
    },
    [],
  );

  /**
   * @function stopEditing
   * @description Leaves edit mode, removing the vertex handles from the map. Safe to call when not
   * editing, so steps can call it unconditionally before committing.
   * @returns {void}
   */
  const stopEditing = () => {
    stopHandlesRef.current?.();
    stopHandlesRef.current = null;
    setIsEditing(false);
  };

  /**
   * @function setShape
   * @description Points the editing at a (re)created shape, and mounts the toggle button on the map
   * the first time there is something to edit. Passing `null` clears it, e.g. when the user undoes,
   * or when the area is one the handles cannot edit - several polygons read from a KML.
   * @param {google.maps.Polygon|google.maps.Rectangle|google.maps.Polyline|null} overlay
   * @param {('polygon'|'rectangle'|'polyline')} [type] - Defaults to the overlay's own type.
   * @returns {void}
   */
  const setShape = (overlay, type) => {
    stopEditing();
    overlayRef.current = overlay ?? null;
    setHasShape(Boolean(overlay));
    typeRef.current = type || (overlay ? Map.getOverlayType(overlay) : 'polygon');
    setGeometryError('');

    if (!enabled || !overlay || containerRef.current) return;

    const container = document.createElement('div');
    container.style.margin = '10px';
    containerRef.current = container;
    removeControlRef.current = Map.addCustomControl(container, position);
    setControlContainer(container);
  };

  /**
   * @function toggleEdit
   * @description Turns the shape's vertex handles on and off, so a bad point can be fixed instead of
   * redrawing the whole shape. Every drag is validated and forwarded to `onChange`.
   * **Called by:
   * @see {ShapeEditControl} - via the edit button's onClick.
   * @returns {void}
   */
  const toggleEdit = () => {
    if (!enabled || !overlayRef.current) return;

    if (isEditing) {
      stopEditing();
      return;
    }

    stopHandlesRef.current = Map.enableVertexEditing(overlayRef.current, typeRef.current, (coords) => {
      const validateShape = validateRef.current || defaultValidate;
      setGeometryError(validateShape(coords, typeRef.current, t));
      onChangeRef.current?.(coords);
    });
    setIsEditing(true);
  };

  return { isEditing, geometryError, controlContainer, hasShape, setShape, stopEditing, toggleEdit };
};
